import { toNumber } from '../../../utils/numbers';
import logger from '../../../utils/logger';

export const MINIMUM_JOG_OPEN_TIME = 0.1;
export const MAXIMUM_JOG_OPEN_TIME = 2.5;
const DEFAULT_JOG_OPEN_TIME = 0.5;
const JOG_OPEN_TIME_RANGE = 0.2;
const JOG_OPEN_TIME_STEP = 0.05;

interface JogData {
  jogWeight: number;
  jogOpenTime: number;
}

export class DynamicJog {
  target: number;
  weight: number;
  initialJogOpenTime: number;
  jogOpenTime: number;
  jogData: JogData[];

  constructor(target: number, initialJogOpenTime?: number, weight?: number) {
    this.target = Number.isFinite(target) ? target : 0;
    this.weight = weight && Number.isFinite(weight) ? weight : 0;
    this.initialJogOpenTime = initialJogOpenTime ?? DEFAULT_JOG_OPEN_TIME;
    this.jogOpenTime = this.initialJogOpenTime > MAXIMUM_JOG_OPEN_TIME ? MAXIMUM_JOG_OPEN_TIME : this.initialJogOpenTime;
    this.jogData = [];
    logger.debug('dynamic jog init', this);
  }

  getJogOpenTime(currentWeight: number): number {
    // Validate the provided weight
    if (!Number.isFinite(currentWeight)) {
      logger.warn('weight for dynamic jog is not valid', currentWeight);
      return 0;
    }
    // Validate the target value
    if (!this.target) {
      logger.warn('target for dynamic jog is not valid', this.target);
      return 0;
    }

    // Main jog logic depending on the current weight
    if (currentWeight > 0) {
      if (this.weight > 0) {
        // If a previous weight exists, calculate jog weight and store jog data
        const jogWeight = currentWeight - this.weight;
        this.jogData.push({ jogWeight, jogOpenTime: this.jogOpenTime });
        if (this.jogData.length > 5) {
          // Limit jogData history to last 5 entries
          this.jogData.shift();
        }
        this.weight = currentWeight;
      } else {
        // First jog, set initial weight and return jog open time
        this.weight = currentWeight;
        return this.jogOpenTime;
      }
    } else {
      // If current weight is not positive, increment jog open time with a cap
      if (this.jogOpenTime >= MAXIMUM_JOG_OPEN_TIME) {
        this.jogOpenTime = toNumber(MAXIMUM_JOG_OPEN_TIME, 2);
      } else {
        this.jogOpenTime = toNumber(this.jogOpenTime + JOG_OPEN_TIME_STEP, 2);
      }
      return this.jogOpenTime;
    }

    const remainingTarget = (this.target - this.weight) * 0.25;
    const lastJog = this.jogData.at(-1) as JogData;

    // Determine jog open time adjustments based on current state
    if (this.weight >= this.target) {
      // If target reached, set minimum jog open time
      this.jogOpenTime = MINIMUM_JOG_OPEN_TIME;
    } else if (lastJog?.jogWeight < remainingTarget) {
      // If last jog was less than a quarter of remaining target, increase jog open time
      if (this.jogOpenTime >= this.initialJogOpenTime + JOG_OPEN_TIME_RANGE || this.jogOpenTime >= MAXIMUM_JOG_OPEN_TIME) {
        this.jogOpenTime = toNumber(this.jogOpenTime, 2);
      } else {
        this.jogOpenTime = toNumber(lastJog.jogOpenTime + JOG_OPEN_TIME_STEP, 2);
      }
    } else if (lastJog?.jogWeight > remainingTarget && lastJog.jogOpenTime > JOG_OPEN_TIME_STEP) {
      // If last jog exceeded a quarter of remaining target, decrease jog open time
      if (this.jogOpenTime <= this.initialJogOpenTime - JOG_OPEN_TIME_RANGE || this.jogOpenTime <= MINIMUM_JOG_OPEN_TIME) {
        this.jogOpenTime = toNumber(this.jogOpenTime, 2);
      } else {
        this.jogOpenTime = toNumber(lastJog.jogOpenTime - JOG_OPEN_TIME_STEP, 2);
      }
    }

    logger.log('dynamic jog:', this.initialJogOpenTime, '->', this.jogOpenTime, 'target:', this.target, 'weight:', this.weight);
    return this.jogOpenTime;
  }
}
